'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useState } from 'react';
import { BookOpen, CreditCard, KeyRound } from 'lucide-react';

import { Button } from '#/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '#/components/ui/dialog';
import { completeOnboarding } from '#/actions/server/onboarding';
import { ROUTES } from '#/data/constants';
import { useT } from '#/i18n/client';

// 결제·카드 등록 화면에서는 안내가 흐름을 끊으므로 띄우지 않는다.
const SKIP_PATHS = [
  ROUTES.PRODUCT_CHECKOUT,
  ROUTES.PAYMENT,
  ROUTES.BILLING_CARD,
  ROUTES.LOGIN,
  ROUTES.REGISTER,
  ROUTES.LOGOUT
];

const STEPS = [
  { icon: KeyRound, key: 'step1', href: ROUTES.API_KEY },
  { icon: CreditCard, key: 'step2', href: ROUTES.PRODUCT },
  { icon: BookOpen, key: 'step3', href: ROUTES.DOCS }
] as const;

/**
 * 로그인 후 처음 한 번 보여 주는 시작 안내 (#297).
 *
 * 마쳤는지는 DB(users.onboarded_at)에 기록한다. 어느 브라우저에서 봤든 한 번만 뜬다.
 * 닫기, 나중에, 단계 버튼 어느 쪽이든 마친 것으로 친다.
 */
export function OnboardingDialog() {
  const t = useT('common');
  const pathname = usePathname();
  const [open, setOpen] = useState(true);

  const skipped = SKIP_PATHS.some((p) => pathname?.startsWith(p));

  const dismiss = () => {
    setOpen(false);
    // 기록이 실패해도 이번 화면에서는 닫힌다. 다음 로그인에 한 번 더 뜰 뿐이다.
    completeOnboarding().catch(() => {});
  };

  return (
    <Dialog open={open && !skipped} onOpenChange={(next) => !next && dismiss()}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{t('onboarding.title')}</DialogTitle>
          <DialogDescription>
            {t('onboarding.description')}
          </DialogDescription>
        </DialogHeader>

        <ol className="grid gap-3">
          {STEPS.map(({ icon: Icon, key, href }, i) => (
            <li
              key={href}
              className="flex items-start gap-3 rounded-lg border p-3"
            >
              <Icon className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
              <div className="flex-1 text-sm">
                <p className="font-medium">
                  {i + 1}. {t(`onboarding.${key}Title`)}
                </p>
                <p className="mt-1 text-muted-foreground">{t(`onboarding.${key}Body`)}</p>
              </div>
              <Button asChild variant="outline" size="sm" onClick={dismiss}>
                <Link href={href}>{t(`onboarding.${key}Cta`)}</Link>
              </Button>
            </li>
          ))}
        </ol>

        <DialogFooter>
          <Button variant="ghost" onClick={dismiss}>
            {t('onboarding.later')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
